import { getUiLocale } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Stage Timeline Component - Horizontal timeline with dots
 * Matches the mockup design with filled/outline dots and connecting line
 * Now includes +Add Task button with popover menu
 */
const OnboardingStageTimeline = ({
    stages = ['Before Day One', 'Day One', 'First Week', 'First Month', '90-Day Review'],
    activeStage,
    onStageClick,
    startDate,
    stageProgress = {},
    onAddAutomatedTask,
    onAddHumanTask,
    lightMode = false,
    canAddTasks = true
}) => {
  useUiLanguage();
    const { t } = useTranslation('onboarding');
    const [showAddMenu, setShowAddMenu] = useState(false);
    const menuRef = useRef(null);
    const getStageLabel = (stage) => {
        const stageMap = {
            'Before Day One': 'categories.beforeDayOne',
            'Day One': 'categories.dayOne',
            'First Week': 'categories.firstWeek',
            'First Month': 'categories.firstMonth',
            '90-Day Review': 'categories.ninetyReview'
        };
        return t(stageMap[stage] || stage, stage);
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowAddMenu(false);
            }
        };
        if (showAddMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAddMenu]);

    // Calculate days until start date
    const getDaysUntilStart = () => {
        if (!startDate) return null;
        const start = new Date(startDate);
        const now = new Date();
        const diffTime = start - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Format the start date nicely
    const formatStartDate = () => {
        if (!startDate) return null;
        const date = new Date(startDate);
        return date.toLocaleDateString(getUiLocale(), {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const daysUntil = getDaysUntilStart();
    const formattedDate = formatStartDate();
    const activeStageIndex = stages.indexOf(activeStage);
    const previousStage = activeStageIndex > 0 ? stages[activeStageIndex - 1] : null;
    const nextStage = activeStageIndex >= 0 && activeStageIndex < stages.length - 1 ? stages[activeStageIndex + 1] : null;

    // Determine stage status
    const getStageStatus = (stage, index) => {
        const progress = stageProgress[stage] || { total: 0, completed: 0 };
        if (stage === activeStage) {
            return 'active';
        }
        if (progress.total > 0 && progress.completed === progress.total) return 'completed';
        return progress.completed > 0 ? 'in-progress' : 'pending';
    };

    const handleAddAutomatedClick = () => {
        setShowAddMenu(false);
        onAddAutomatedTask?.();
    };

    const handleAddHumanClick = () => {
        setShowAddMenu(false);
        onAddHumanTask?.();
    };

    return (
        <div className="stage-timeline">
            {/* Header row with title, start date, and add button */}
            <div className="timeline-header">
                <div className="timeline-header-left">
                    <span className="timeline-title" style={lightMode ? { color: '#1e293b' } : {}}>{t('workflow.timelineTitle', 'Onboarding Progress')}</span>
                    <span className="timeline-subtitle" style={lightMode ? { color: '#64748b' } : {}}>{t('workflow.timelineSubtitle', 'Click on an onboarding stage to see the tasks')}</span>
                </div>

                <div className="timeline-header-right">
                    {/* Start Date Badge - First */}
                    {formattedDate && (
                        <div className="start-date-badge">
                            <i className="fa-regular fa-calendar" />
                            <span>
                                {formattedDate}
                                {daysUntil !== null && ` (${daysUntil > 0 ? uiText("{{count}} days", { count: daysUntil }) : daysUntil === 0 ? uiText("Today!") : uiText("{{count}} days ago", { count: Math.abs(daysUntil) })})`}
                            </span>
                        </div>
                    )}

                    {/* +Add Task Button with Popover Menu - After */}
                    {canAddTasks && (
                        <div className="add-task-container" ref={menuRef}>
                            <button
                                className="btn-add-task-timeline"
                                onClick={() => setShowAddMenu(!showAddMenu)}
                            >
                                <i className="fa-solid fa-plus" />
                                <span>{t('buttons.addTask', 'Add Task')}</span>
                            </button>

                            {/* Popover Menu */}
                            {showAddMenu && (
                                <div className="add-task-menu">
                                    <button
                                        className="add-menu-item"
                                        onClick={handleAddAutomatedClick}
                                    >
                                        <i className="fa-solid fa-bolt" />
                                        <span>{t('workflow.automatedTask', 'Automated Task')}</span>
                                    </button>
                                    <button
                                        className="add-menu-item"
                                        onClick={handleAddHumanClick}
                                    >
                                        <i className="fa-solid fa-user" />
                                        <span>{t('workflow.teamTask', 'Manual Task')}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Timeline stages with horizontal line */}
            <div className="timeline-stage-nav-row">
                <button
                    type="button"
                    className="timeline-nav-btn"
                    disabled={!previousStage}
                    onClick={() => previousStage && onStageClick?.(previousStage)}
                >
                    <i className="fa-solid fa-chevron-left" />
                    <span>{t('workflow.previousStage', 'Prev')}</span>
                </button>

                <div className="timeline-stages">
                    {stages.map((stage, index) => {
                        const status = getStageStatus(stage, index);

                        return (
                            <div
                                key={stage}
                                className={`stage-node ${status}`}
                                onClick={() => onStageClick?.(stage)}
                            >
                                <div className="stage-circle">
                                    {status === 'completed' && <i className="fa-solid fa-check" />}
                                </div>
                                <span className="stage-label" style={lightMode ? { color: '#64748b' } : {}}>{getStageLabel(stage)}</span>
                            </div>
                        );
                    })}
                </div>

                <button
                    type="button"
                    className="timeline-nav-btn"
                    disabled={!nextStage}
                    onClick={() => nextStage && onStageClick?.(nextStage)}
                >
                    <span>{t('workflow.nextStage', 'Next')}</span>
                    <i className="fa-solid fa-chevron-right" />
                </button>
            </div>
        </div>
    );
};

export default OnboardingStageTimeline;
